
import { useState, useEffect, useRef } from 'react';
import { useQueryClient } from 'react-query';
import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router-dom';
import { Spin, Input, Button, Typography, Form } from "antd";
import { LoadingOutlined } from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import useRegisterFarmForm from './useRegisterFarmForm';
import { TFunction } from 'types';
import { useLogin } from 'hooks/useLogin';
import { cookies, storageKeys } from 'static';
import { setAnalyticsFarmContext, trackAnalyticsEvent, trackLoginSuccess } from 'analytics/ga4';
import { accountInfoQueryKey, getAccountInfo } from 'api/account';
import { getStringValue, hasRegisteredFarm } from 'farmContext';

const FARM_REGISTERED_ZAPIER_WEBHOOK_URL = 'https://hooks.zapier.com/hooks/catch/14247483/2go0qbs/';
const REGISTER_FARM_STEP_NAMES = ['farm_name', 'person_name', 'company_info', 'address'] as const;
const LIMITED_COMPANY_ORG_NUMBER_REGEX = /^5\d[2-9]\d{3}-?\d{4}$/;
const getCompanyTypeFromRegistration = (companyRegistration: string) =>
  LIMITED_COMPANY_ORG_NUMBER_REGEX.test(companyRegistration.trim()) ? 'O' : 'P';

export default function RegisterFarmForm() {

  const { t }: TFunction = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [loginForm] = Form.useForm();
  const { mutate: login, isLoading: isLoggingIn, isError: loginFailed } = useLogin();
  const storedAuth = localStorage.getItem(cookies.FarmersPlanToken);
  const emailForLogin = storedAuth ? JSON.parse(storedAuth)?.username ?? '' : '';

  const {
    failedFetchingAccountInfo,
    hasFetchedAccountInfo,
    selfAddFarmAsync,
  } = useRegisterFarmForm();

  const [step, setStep] = useState(0);
  const [isFinishingFarm, setIsFinishingFarm] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const firstInputRef = useRef<any>(null);
  const [farmName, setFarmName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyRegistration, setCompanyRegistration] = useState('');
  const [address1, setAddress1] = useState('');
  const [city, setCity] = useState('');
  const [orgError, setOrgError] = useState('');

  useEffect(() => {
    setTimeout(() => firstInputRef.current?.focus(), 50);
    trackAnalyticsEvent('reg_flow_register_farm_step', {
      step_index: step + 1,
      step_name: REGISTER_FARM_STEP_NAMES[step] || 'unknown',
    });
  }, [step]);

  useEffect(() => {
    trackAnalyticsEvent('reg_flow_register_farm_view');
  }, []);

  useEffect(() => {
    if (saveFailed) {
      trackAnalyticsEvent('reg_flow_register_farm_error', {
        error_stage: 'create_farm',
      });
    }
  }, [saveFailed]);

  const btnStyle = { width: '100%', fontSize: 14, backgroundColor: '#ff6b3d', height: 46 };
  const validateOrgNumber = (value: string) => /^\d{6}-?\d{4}$/.test(value.trim());

  const getFarmIdFromResponse = (data: any): string | undefined => {
    const candidates = [data?.id, data?.farmId, data?.farm?.id, data?.farm?.farmId];
    const farmId = candidates.find((candidate) => candidate !== undefined && candidate !== null && `${candidate}`.length > 0);

    return farmId ? String(farmId) : undefined;
  };

  const notifyZapierFarmRegistered = async (farmId: string | undefined, orgNumber: string) => {
    try {
      await axios.get(FARM_REGISTERED_ZAPIER_WEBHOOK_URL, {
        params: {
          date: moment().format('YYYY-MM-DD HH:mm:ss'),
          email: emailForLogin,
          farmId: farmId ?? '',
          farmName: farmName.trim(),
          orgNumber,
          companyName: companyName.trim(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          address: address1.trim(),
          city: city.trim(),
        },
      });
    } catch (error) {
      console.error('Error sending data to Zapier:', error);
    }
  };

  const completeFarmRegistration = (data: any) => {
    const farmId = getFarmIdFromResponse(data);
    const normalizedCompanyRegistration = companyRegistration.trim();

    void notifyZapierFarmRegistered(farmId, normalizedCompanyRegistration);

    if (farmId) {
      sessionStorage.setItem(storageKeys.RecentFarmId, farmId);
      if (farmName?.trim()) {
        sessionStorage.setItem(storageKeys.RecentFarmName, farmName.trim());
      }
    }
    if (normalizedCompanyRegistration) {
      sessionStorage.setItem(storageKeys.RecentFarmOrgNumber, normalizedCompanyRegistration);
    } else {
      sessionStorage.removeItem(storageKeys.RecentFarmOrgNumber);
    }
    setAnalyticsFarmContext(
      farmId,
      farmName.trim() || undefined,
      normalizedCompanyRegistration || undefined,
    );
    sessionStorage.setItem(storageKeys.FarmOnboardingRegistrationDate, new Date().toISOString());
    if (farmId) {
      sessionStorage.setItem(storageKeys.FarmOnboardingRegistrationFarmId, farmId);
    } else {
      sessionStorage.removeItem(storageKeys.FarmOnboardingRegistrationFarmId);
    }
    sessionStorage.setItem(storageKeys.FarmOnboardingCelebrationPending, 'true');
    if (farmId) {
      sessionStorage.setItem(storageKeys.FarmOnboardingCelebrationFarmId, farmId);
    } else {
      sessionStorage.removeItem(storageKeys.FarmOnboardingCelebrationFarmId);
    }

    trackAnalyticsEvent('reg_flow_register_farm_success', {
      has_prod_places: false,
    });

    void queryClient.invalidateQueries(accountInfoQueryKey());
    void queryClient.invalidateQueries('crops-user-companies');

    navigate('/app-selection', {
      replace: true,
      state: {
        newFarmId: farmId,
      },
    });
  };

  const wait = (milliseconds: number) => new Promise((resolve) => {
    window.setTimeout(resolve, milliseconds);
  });

  const findCreatedFarmOnAccount = async () => {
    const accountInfo = await getAccountInfo();

    if (!hasRegisteredFarm(accountInfo)) {
      return null;
    }

    const normalizedFarmName = farmName.trim().toLowerCase();
    const normalizedOrg = companyRegistration.trim().replace(/-/g, '');

    const matchedFarm = (accountInfo.userFarms ?? []).find((farm) => {
      const farmId = getStringValue(farm.id ?? farm.farmId);

      if (!farmId) {
        return false;
      }

      const name = getStringValue(farm.farmName).toLowerCase();
      const org = getStringValue(farm.companyRegistration).replace(/-/g, '');

      if (normalizedFarmName && name === normalizedFarmName) {
        return true;
      }

      if (normalizedOrg && org === normalizedOrg) {
        return true;
      }

      return false;
    });

    if (!matchedFarm) {
      return null;
    }

    return { id: getStringValue(matchedFarm.id ?? matchedFarm.farmId) };
  };

  const findCreatedFarmAfterSave = async () => {
    const attempts = 8;

    for (let attempt = 0; attempt < attempts; attempt += 1) {
      const recoveredFarm = await findCreatedFarmOnAccount();

      if (recoveredFarm) {
        return recoveredFarm;
      }

      if (attempt < attempts - 1) {
        await wait(2500);
      }
    }

    return null;
  };

  const handleSubmit = async () => {
    if (isFinishingFarm) {
      return;
    }

    setSaveFailed(false);
    setIsFinishingFarm(true);
    trackAnalyticsEvent('reg_flow_register_farm_submit', {
      prod_place_count: 0,
      skipped_prod_place: true,
    });

    const farmPayload = {
      addressData: { address1, city, district: '', zipCode: '' },
      contactData: {
        companyName,
        companyRegistration,
        companyType: getCompanyTypeFromRegistration(companyRegistration),
        firstName,
        lastName,
      },
      countryId: 45,
      farmName,
      prodPlaceData: [],
    };

    try {
      const data = await selfAddFarmAsync(farmPayload);
      completeFarmRegistration(data);
    } catch {
      try {
        const recoveredFarm = await findCreatedFarmAfterSave();

        if (recoveredFarm) {
          completeFarmRegistration(recoveredFarm);
          return;
        }
      } catch {
        // Account lookup failed. Show the save error below.
      }

      setSaveFailed(true);
    } finally {
      setIsFinishingFarm(false);
    }
  };

  if (failedFetchingAccountInfo) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 40, maxWidth: 368, margin: '0 auto' }}>
        <Typography.Text style={{ color: '#ff4d4f', display: 'block', textAlign: 'center', marginBottom: 20 }}>
          {t('AnErrorOccurred')}
        </Typography.Text>
        {loginFailed && (
          <Typography.Text style={{ color: '#ff4d4f', display: 'block', textAlign: 'center', marginBottom: 16 }}>
            {t('LoginFailedCheckPassword')}
          </Typography.Text>
        )}
        <Form form={loginForm} layout="vertical" style={{ width: '100%' }} onFinish={(values) => {
          trackAnalyticsEvent('reg_flow_register_farm_login_submit');
          login({ username: emailForLogin, password: values.password }, {
            onSuccess: (data: any) => {
              trackAnalyticsEvent('reg_flow_register_farm_login_success');
              localStorage.setItem(cookies.FarmersPlanToken, JSON.stringify({ token: data?.token, username: emailForLogin }));
              void trackLoginSuccess(emailForLogin);
              window.location.reload();
            },
            onError: () => {
              trackAnalyticsEvent('reg_flow_register_farm_login_error');
            },
          });
        }}>
          <Form.Item name="email" initialValue={emailForLogin}>
            <Input disabled size="large" />
          </Form.Item>
          <Form.Item name="password" rules={[{ required: true, message: t('PasswordIsRequired') }]}>
            <Input.Password placeholder={t('Password')} size="large" style={{ padding: '0px 10px 0px 0px' }} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" size="large" htmlType="submit" loading={isLoggingIn}
              style={{ width: '100%', fontSize: 14, backgroundColor: '#ff6b3d', height: 46 }}>
              {t('ContinueToFarm')}
            </Button>
          </Form.Item>
        </Form>
      </div>
    );
  }

  if (!hasFetchedAccountInfo) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 40 }}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 40 }} spin />} spinning={true} />
      </div>
    );
  }

  const totalSteps = 4;
  const quoteStyle = {
    textAlign: 'center' as const, color: '#555', fontSize: 14,
    fontStyle: 'italic' as const, backgroundColor: '#fff', borderRadius: 12,
    padding: '16px 20px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: 32,
  };
  const quoteAuthorStyle = { display: 'block', fontStyle: 'normal' as const, color: '#aaa', fontSize: 12, marginTop: 6 };
  const progressStyle = { textAlign: 'center' as const, color: '#bbb', fontSize: 13, marginBottom: 8, letterSpacing: 1 };

  const quotes = [
    { text: t('CustomerQuote1'), author: t('CustomerQuote1Author') },
    { text: t('CustomerQuote2'), author: t('CustomerQuote2Author') },
    { text: t('CustomerQuote3'), author: t('CustomerQuote3Author') },
    { text: t('CustomerQuote4'), author: t('CustomerQuote4Author') },
  ];

  const customerQuote = (index: number) => (
    <div style={quoteStyle}>
      "{quotes[index].text}"
      <span style={quoteAuthorStyle}>— {quotes[index].author}</span>
    </div>
  );

  const backLink = (onClick: () => void) => (
    <Typography.Link onClick={onClick} style={{ display: 'block', textAlign: 'center', marginTop: 14, color: '#999', fontSize: 14 }}>
      {t('Back')}
    </Typography.Link>
  );

  const gdprNotice = (
    <div style={{ textAlign: 'center', marginTop: 20, color: '#aaa', fontSize: 12, lineHeight: 1.5 }}>
      🔒 {t('GDPRNotice')}
    </div>
  );

  const stepIndicator = (current: number) => (
    <div style={progressStyle}>
      {t('StepOf', { current, total: totalSteps })}
      <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 8 }}>
        {Array.from({ length: totalSteps }, (_, i) => (
          <div key={i} style={{
            width: 32, height: 4, borderRadius: 2,
            backgroundColor: i < current ? '#ff6b3d' : '#e0e0e0',
            transition: 'background-color 0.3s',
          }} />
        ))}
      </div>
    </div>
  );

  return (
    <div style={{ paddingTop: 40 }}>

        {step === 0 && (
          <>
            {customerQuote(0)}
            {stepIndicator(1)}
            <Typography.Title level={1} style={{ textAlign: 'center' }}>
              {t('WhatIsYourFarmName')}
            </Typography.Title>
            <Input
              size="large"
              placeholder={t('FarmName')}
              value={farmName}
              onChange={(e) => setFarmName(e.target.value)}
              onPressEnter={() => farmName.trim() && setStep(1)}
              ref={firstInputRef}
            />
            <Button type="primary" size="large" disabled={!farmName.trim()}
              style={{ ...btnStyle, marginTop: 16 }} onClick={() => setStep(1)}>
              {t('Next')}
            </Button>
            {gdprNotice}
            {backLink(() => navigate('/app-selection'))}
          </>
        )}

        {step === 1 && (
          <>
            {customerQuote(1)}
            {stepIndicator(2)}
            <Typography.Title level={1} style={{ textAlign: 'center' }}>
              {t('WhatIsYourName')}
            </Typography.Title>
            <Input
              size="large"
              placeholder={t('FirstName')}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              style={{ marginBottom: 12 }}
              ref={firstInputRef}
            />
            <Input
              size="large"
              placeholder={t('LastName')}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              onPressEnter={() => firstName.trim() && lastName.trim() && setStep(2)}
            />
            <Button type="primary" size="large" disabled={!firstName.trim() || !lastName.trim()}
              style={{ ...btnStyle, marginTop: 16 }} onClick={() => setStep(2)}>
              {t('Next')}
            </Button>
            {gdprNotice}
            {backLink(() => setStep(0))}
          </>
        )}

        {step === 2 && (
          <>
            {customerQuote(2)}
            {stepIndicator(3)}
            <Typography.Title level={1} style={{ textAlign: 'center' }}>
              {t('WhatIsYourCompanyInfo')}
            </Typography.Title>
            <Input
              size="large"
              placeholder={t('CompanyName')}
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              style={{ marginBottom: 12 }}
              ref={firstInputRef}
            />
            <Input
              size="large"
              placeholder={t('CompanyRegistration')}
              value={companyRegistration}
              onChange={(e) => { setCompanyRegistration(e.target.value); setOrgError(''); }}
              onPressEnter={() => {
                if (companyName.trim() && companyRegistration.trim()) {
                  if (!validateOrgNumber(companyRegistration)) { setOrgError(t('InvalidOrgNumber')); return; }
                  setStep(3);
                }
              }}
            />
            {orgError && (
              <Typography.Text style={{ color: '#ff4d4f', display: 'block', marginTop: 8 }}>
                {orgError}
              </Typography.Text>
            )}
            <Button type="primary" size="large" disabled={!companyName.trim() || !companyRegistration.trim()}
              style={{ ...btnStyle, marginTop: 16 }} onClick={() => {
                if (!validateOrgNumber(companyRegistration)) { setOrgError(t('InvalidOrgNumber')); return; }
                setStep(3);
              }}>
              {t('Next')}
            </Button>
            {gdprNotice}
            {backLink(() => setStep(1))}
          </>
        )}

        {step === 3 && (
          <>
            {customerQuote(3)}
            {stepIndicator(4)}
            <Typography.Title level={1} style={{ textAlign: 'center' }}>
              {t('WhatIsYourAddress')}
            </Typography.Title>
            <Input
              size="large"
              placeholder={t('Address')}
              value={address1}
              onChange={(e) => setAddress1(e.target.value)}
              style={{ marginBottom: 12 }}
              ref={firstInputRef}
              disabled={isFinishingFarm}
            />
            <Input
              size="large"
              placeholder={t('City')}
              value={city}
              onChange={(e) => setCity(e.target.value)}
              onPressEnter={() => address1.trim() && city.trim() && !isFinishingFarm && handleSubmit()}
              disabled={isFinishingFarm}
            />
            {isFinishingFarm && (
              <Typography.Text style={{ display: 'block', marginTop: 12, textAlign: 'center', color: '#666' }}>
                {t('CreatingYourFarm')}
              </Typography.Text>
            )}
            {saveFailed && !isFinishingFarm && (
              <Typography.Text style={{ color: '#ff4d4f', display: 'block', marginTop: 8, textAlign: 'center' }}>
                {t('AnErrorOccurred')}
              </Typography.Text>
            )}
            <Button
              type="primary"
              size="large"
              disabled={!address1.trim() || !city.trim() || isFinishingFarm}
              loading={isFinishingFarm}
              style={{ ...btnStyle, marginTop: 16 }}
              onClick={handleSubmit}
            >
              {t('Continue')}
            </Button>
            {gdprNotice}
            {!isFinishingFarm ? backLink(() => setStep(2)) : null}
          </>
        )}

    </div>
  );
}
